import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Space, Tabs, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';

import useInterval from 'services/useInterval';

import Builder from './Builder';
import ComputeService, { LaneStrip } from './ComputeService';
import CorpusNews from './CorpusNews';
import DraftDetail from './DraftDetail';
import Drafts from './Drafts';
import ScenarioErrorBoundary from './ErrorBoundary';
import Glossary from './Glossary';
import Jobs, { usePolling } from './Jobs';
import JobDetail from './JobDetail';
import Problems from './Problems';
import RegistryCoverage from './RegistryCoverage';
import {
  fetchCompute,
  fetchFleetOverview,
  fetchProblems,
  FleetOverview,
} from './api';
import { readableError } from './errors';
import { ComputeSnapshot, ProblemDocument } from './types';
// The assessment admin stylesheet carries the shared table/json/layout
// classes this module reuses (assessment-admin, assessment-table,
// assessment-json, ...); importing it here keeps the two admin surfaces
// visually identical without duplicating CSS.
import '../Assessments/styles.css';

/**
 * The problem document is a fleet read, not a job: it is worth a minute of
 * staleness and is not worth asking for every three seconds.
 */
const PROBLEMS_POLL_MS = 60000;

/** The tabs, in the order they are rendered. An unknown `?tab=` falls back to
 *  the first rather than rendering an empty panel. */
const TAB_KEYS = [
  'drafts',
  'builder',
  'corpus-news',
  'registry-coverage',
  'jobs',
  'problems',
  'compute',
];

const ScenarioBuilderAdmin: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selectedDraft = params.get('draft');
  const selectedJob = params.get('job');

  // The tab is addressable, because the Problems screen's remedies point at
  // three of them ("Open the jobs list", "Open registry coverage", "Open the
  // compute card") and it renders a remedy as a react-router Link. A tab held
  // only in component state cannot be linked to, so those remedies used to
  // name API paths that the SPA router drops on the dashboard redirect.
  const requested = params.get('tab') || '';
  const tab = TAB_KEYS.includes(requested) ? requested : 'drafts';
  const setTab = (key: string) => goTo({ tab: key === 'drafts' ? '' : key });

  /** Navigate within the module, keeping every parameter not named here —
   *  the screens' own filters live in this query string too. */
  const goTo = (patch: Record<string, string>, { replace = false } = {}) => {
    const next = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === '') next.delete(key);
      else next.set(key, value);
    });
    const search = next.toString();
    const url = `/dashboard/scenario-builder${search ? `?${search}` : ''}`;
    // A one-shot parameter that has done its job is spent, not navigated
    // away from: pushing it would make Back re-open the drawer it just
    // closed.
    if (replace) history.replace(url);
    else history.push(url);
  };
  const [compute, setCompute] = useState<ComputeSnapshot | null>(null);
  const [computeError, setComputeError] = useState('');
  const [computeLoading, setComputeLoading] = useState(false);
  const [problems, setProblems] = useState<ProblemDocument | null>(null);
  const [problemsError, setProblemsError] = useState('');
  const [problemsLoading, setProblemsLoading] = useState(false);
  const [overview, setOverview] = useState<FleetOverview | null>(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  const loadCompute = useCallback(async (showSpinner = false) => {
    if (showSpinner) setComputeLoading(true);
    try {
      setCompute(await fetchCompute());
      setComputeError('');
    } catch (requestError) {
      setComputeError(
        readableError(requestError, {
          what: 'The compute service',
        }),
      );
    } finally {
      if (showSpinner) setComputeLoading(false);
    }
  }, []);

  const loadProblems = useCallback(async (showSpinner = false) => {
    if (showSpinner) setProblemsLoading(true);
    try {
      setProblems(await fetchProblems());
      setProblemsError('');
    } catch (requestError) {
      setProblemsError(
        readableError(requestError, {
          what: 'The problem document',
        }),
      );
    } finally {
      if (showSpinner) setProblemsLoading(false);
    }
  }, []);

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await fetchFleetOverview());
    } catch (_requestError) {
      // The counts are context, not the screen. A module that refused to
      // render its tabs because a headline number could not be read would be
      // worse than one whose sentence stops short.
      setOverview(null);
    }
  }, []);

  useEffect(() => {
    void loadCompute();
    void loadProblems();
    void loadOverview();
  }, [loadCompute, loadProblems, loadOverview]);

  // Three seconds while the lane is moving or work is queued behind it,
  // thirty when nothing is happening.
  const computeMoving =
    compute?.state === 'starting' ||
    compute?.state === 'stopping' ||
    Boolean(compute?.slim?.pending_jobs) ||
    Boolean(compute?.blocked_jobs);
  usePolling(() => void loadCompute(), { live: computeMoving });

  useInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    void loadProblems();
  }, PROBLEMS_POLL_MS);

  // OPENING A ROW IS NAVIGATION WITHIN THE MODULE, NOT A FRESH START.
  //
  // These three used to write the address from scratch, which threw away the
  // tab and every screen's filters on the way into a detail view and again on
  // the way out -- so a search typed on the drafts list died the first time
  // anybody opened one of its results, which is precisely the failure the
  // query-string filters were introduced to end. `goTo` keeps everything it is
  // not told to change. The one thing each must clear is the OTHER kind of
  // row: a draft chip on a job screen has to leave the job screen.
  const openDraft = (id: number) => goTo({ draft: String(id), job: '' });
  const openJob = (id: string) => goTo({ job: id, draft: '' });
  const closeDraft = () => goTo({ draft: '' });
  const closeJob = () => goTo({ job: '' });

  /**
   * Where a target opens. Every branch has to land somewhere real: the panel
   * this serves used to send every chip to `?draft=<id>` whatever the target
   * was, so a blueprint-version id and a null both rendered as "Draft #N" and
   * both landed on "Not found".
   *
   * A blueprint VERSION id is not a blueprint id, so it cannot open the
   * blueprint page; an answer key's id cannot open the blueprint page either,
   * and this module no longer lists answer keys (they are derived, audited
   * and published from the blueprint page). What always resolves for either —
   * and is what an admin actually wants — is the ledger scoped to that
   * target: every job that has run against it, with its refusals and
   * remedies.
   */
  const openTarget = (kind: string, id: number | null) => {
    if (kind === 'scenario_draft' && id != null) return openDraft(id);
    if ((kind === 'job' || kind === 'scenario_job') && id != null) {
      return openJob(String(id));
    }
    if (id != null) {
      return goTo({
        tab: 'jobs',
        'jobs.target_kind': kind,
        'jobs.target_id': String(id),
      });
    }
    return setTab('registry-coverage');
  };

  if (selectedJob) {
    return (
      <ScenarioErrorBoundary what="This job">
        <JobDetail
          key={selectedJob}
          jobId={selectedJob}
          onBack={closeJob}
          onOpen={openJob}
          onOpenTarget={openTarget}
        />
      </ScenarioErrorBoundary>
    );
  }

  if (selectedDraft) {
    return (
      <ScenarioErrorBoundary what="This draft">
        <DraftDetail
          key={selectedDraft}
          draftId={Number(selectedDraft)}
          onBack={closeDraft}
        />
      </ScenarioErrorBoundary>
    );
  }

  const problemCount = problems?.counts?.error || 0;

  return (
    <div className="assessment-admin">
      <div className="assessment-workspace-header">
        <Typography.Title level={2} className="assessment-title">
          Scenario builder
        </Typography.Title>
        <Button
          icon={<QuestionCircleOutlined />}
          onClick={() => setGlossaryOpen(true)}
        >
          What these words mean
        </Button>
      </div>
      {tab !== 'corpus-news' && (
        <LaneStrip
          snapshot={compute}
          error={computeError}
          onOpenCompute={() => setTab('compute')}
        />
      )}
      {tab !== 'corpus-news' && tab !== 'builder' && (
        <Alert
          className="assessment-callout"
          showIcon
          type="info"
          title="Turn an authored news scenario into something trainees can play and be graded on"
          description={
            <Space direction="vertical" size={8}>
              <span>
                A draft is minted into a blueprint; its answer key is derived
                from its blueprint page, and the quality audit says whether that
                key can tell skill from clicking — it goes live when the audit
                passes and stays a draft when it does not. Minting, deriving,
                publishing and the registry seeder all run as recorded jobs:
                every run keeps who asked for it, what it was given, what it
                answered and what it wrote.
              </span>
              {overview && (
                <span data-testid="fleet-counts">
                  <Typography.Text strong>Today: </Typography.Text>
                  {`${overview.drafts} draft${overview.drafts === 1 ? '' : 's'}, `}
                  {`${overview.families} minted into blueprint families, `}
                  {`${overview.live_answer_keys} answer key${
                    overview.live_answer_keys === 1 ? '' : 's'
                  } live, `}
                  {`${overview.passing_gate} passing the quality audit.`}
                </span>
              )}
            </Space>
          }
        />
      )}
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'drafts',
            label: 'Drafts',
            children: (
              <ScenarioErrorBoundary what="The drafts list">
                <Drafts onOpen={openDraft} />
              </ScenarioErrorBoundary>
            ),
          },
          {
            key: 'builder',
            label: 'Builder',
            // Mounted only while open: the chat polls while a turn runs.
            children:
              tab === 'builder' ? (
                <ScenarioErrorBoundary what="The blueprint builder">
                  <Builder />
                </ScenarioErrorBoundary>
              ) : null,
          },
          {
            key: 'corpus-news',
            label: 'Corpus & news',
            children:
              tab === 'corpus-news' ? (
                <ScenarioErrorBoundary what="Corpus and news">
                  <CorpusNews onOpenJob={openJob} />
                </ScenarioErrorBoundary>
              ) : null,
          },
          {
            key: 'registry-coverage',
            label: 'Registry coverage',
            children: (
              <ScenarioErrorBoundary what="Registry coverage">
                <RegistryCoverage />
              </ScenarioErrorBoundary>
            ),
          },
          {
            key: 'jobs',
            label: 'Jobs',
            children: (
              <ScenarioErrorBoundary what="The job ledger">
                <Jobs
                  onOpen={openJob}
                  onOpenTarget={openTarget}
                  onOpenCompute={() => setTab('compute')}
                />
              </ScenarioErrorBoundary>
            ),
          },
          {
            key: 'problems',
            // The count rides on the tab so it is visible without the tab
            // being opened — the whole point of a fleet health view.
            label: (
              <Space size={8}>
                Problems
                <Badge count={problemCount} data-testid="problems-badge" />
              </Space>
            ),
            children: (
              <ScenarioErrorBoundary what="The problem list">
                <Problems
                  report={problems}
                  loading={problemsLoading}
                  error={problemsError}
                  onRefresh={() => loadProblems(true)}
                />
              </ScenarioErrorBoundary>
            ),
          },
          {
            key: 'compute',
            label: 'Compute',
            children: (
              <ScenarioErrorBoundary what="The compute card">
                <ComputeService
                  snapshot={compute}
                  error={computeError}
                  loading={computeLoading}
                  onRefresh={() => loadCompute(true)}
                />
              </ScenarioErrorBoundary>
            ),
          },
        ]}
      />
      <Glossary open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
    </div>
  );
};

export default ScenarioBuilderAdmin;
